import classNames from "classnames";
import React, { useContext, useEffect, useRef, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useLocation } from "react-router-dom";
import { AuthContext } from "../../../contexts/Auth";
import { Button } from "../../button/Button";
import { Clickable } from "../../button/Clickable";
import { Icon } from "../../graphic/Icon";
import { TextLogo } from "../../graphic/TextLogo";
import { MeMenu } from "../../layout/Layout/MeMenu";
import { Spinner } from "../../loaders/Spinner";
import { FRONT_LINKS, FRONT_MODULES, getModulePageLink } from "../../../utils/frontConstants";
import { SiteLanguageMenu } from "../SiteLanguageMenu";
import { useSiteLink } from "../../../utils/useSiteLink";
import styles from "./SiteNavbar.scss";

const msgKey = "siteNavbar";
const MOBILE_BREAKPOINT = 900; // Matches $breakpoint--tablet

/**
 * Sticky top navigation for the public pages
 * @param scrollContainer – element that scrolls the page, when it isn't the window (e.g. a fullscreen overlay)
 */
export const SiteNavbar = ({ scrollContainer, className }) => {
    const siteLink = useSiteLink();
    const intl = useIntl();
    const { me, busyAuthenticating } = useContext(AuthContext);
    const [productOpen, setProductOpen] = useState(false);
    const [mobileOpen, setMobileOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const productRef = useRef(null);
    const closeTimer = useRef(null);
    const location = useLocation();

    // Close menus when navigating (the navbar stays mounted between pages using the same layout)
    useEffect(() => {
        setProductOpen(false);
        setMobileOpen(false);
    }, [location.pathname]);

    // Show the bottom border once the page has scrolled
    useEffect(() => {
        const target = scrollContainer || window;
        const onScroll = () => setScrolled((scrollContainer ? scrollContainer.scrollTop : window.scrollY) > 4);
        onScroll();
        target.addEventListener("scroll", onScroll, { passive: true });
        return () => target.removeEventListener("scroll", onScroll);
    }, [scrollContainer]);

    useEffect(() => {
        const onKeyDown = (event) => event.key === "Escape" && setProductOpen(false);
        const onMouseDown = (event) => {
            if (productRef.current && !productRef.current.contains(event.target)) {
                setProductOpen(false);
            }
        };
        window.addEventListener("keydown", onKeyDown);
        document.addEventListener("mousedown", onMouseDown);
        return () => {
            clearTimeout(closeTimer.current);
            window.removeEventListener("keydown", onKeyDown);
            document.removeEventListener("mousedown", onMouseDown);
        };
    }, []);

    // Lock page scroll while the mobile menu is open. Locked on <body>, not <html>: body's overflow then still
    // applies to the window, which keeps the scroll position and the sticky navbar in place.
    useEffect(() => {
        document.body.style.overflow = mobileOpen ? "hidden" : "";
        const onResize = () => window.innerWidth >= MOBILE_BREAKPOINT && setMobileOpen(false);
        window.addEventListener("resize", onResize);
        return () => {
            document.body.style.overflow = "";
            window.removeEventListener("resize", onResize);
        };
    }, [mobileOpen]);

    const showProduct = () => {
        clearTimeout(closeTimer.current);
        setProductOpen(true);
    };

    const hideProduct = () => {
        closeTimer.current = setTimeout(() => setProductOpen(false), 120);
    };

    const renderAccount = (mobile) => {
        if (busyAuthenticating) {
            return <Spinner />;
        }
        if (me) {
            return <MeMenu variant="site" showName={!mobile} />;
        }
        return (
            <>
                <Button link={FRONT_LINKS.login} color="silent" size="small" className={styles.button}>
                    <FormattedMessage id={`${msgKey}.logIn`} />
                </Button>
                {!mobile ? (
                    <Button link={FRONT_LINKS.signup} color="primary" size="small" className={styles.button}>
                        <FormattedMessage id={`${msgKey}.startFreeTrial`} />
                    </Button>
                ) : null}
            </>
        );
    };

    return (
        <header
            className={classNames(
                styles.base,
                {
                    [styles.scrolled]: scrolled || mobileOpen,
                    [styles.menuOpen]: mobileOpen
                },
                className
            )}
        >
            <div className={styles.bar}>
                <div className={styles.start}>
                    <Clickable
                        link={siteLink("/")}
                        className={styles.brand}
                        title={intl.formatMessage({ id: `${msgKey}.home` })}
                    >
                        <TextLogo color="primary" />
                    </Clickable>
                    <nav className={styles.nav} aria-label={intl.formatMessage({ id: `${msgKey}.mainNavigation` })}>
                        <div
                            ref={productRef}
                            className={styles.product}
                            onMouseEnter={showProduct}
                            onMouseLeave={hideProduct}
                        >
                            <button
                                type="button"
                                className={styles.navItem}
                                aria-expanded={productOpen}
                                aria-haspopup="true"
                                onClick={() => setProductOpen(!productOpen)}
                            >
                                <FormattedMessage id={`${msgKey}.product`} />
                            </button>
                            {productOpen ? (
                                <div className={styles.panel} role="menu">
                                    <p className={styles.panelLabel}>
                                        <FormattedMessage id={`${msgKey}.modules`} />
                                    </p>
                                    <div className={styles.panelGrid}>
                                        {FRONT_MODULES.map((module) => (
                                            <Clickable
                                                key={module.slug}
                                                link={siteLink(getModulePageLink(module))}
                                                className={styles.moduleItem}
                                            >
                                                <span className={styles.moduleIcon}>
                                                    <Icon name={module.icon} />
                                                </span>
                                                <span>
                                                    <span className={styles.moduleName}>
                                                        <FormattedMessage id={`frontModules.${module.slug}.name`} />
                                                    </span>
                                                    <span className={styles.moduleDescription}>
                                                        <FormattedMessage
                                                            id={`frontModules.${module.slug}.description`}
                                                        />
                                                    </span>
                                                </span>
                                            </Clickable>
                                        ))}
                                    </div>
                                </div>
                            ) : null}
                        </div>
                        <Clickable link={siteLink(FRONT_LINKS.pricing)} className={styles.navItem}>
                            <FormattedMessage id={`${msgKey}.pricing`} />
                        </Clickable>
                        <Clickable href={FRONT_LINKS.blog} className={styles.navItem}>
                            <FormattedMessage id={`${msgKey}.blog`} />
                        </Clickable>
                        <Clickable link={siteLink(FRONT_LINKS.contact)} className={styles.navItem}>
                            <FormattedMessage id={`${msgKey}.contact`} />
                        </Clickable>
                    </nav>
                </div>
                <div className={styles.end}>
                    <SiteLanguageMenu />
                    {renderAccount(false)}
                </div>
                <div className={styles.endMobile}>
                    <SiteLanguageMenu />
                    {renderAccount(true)}
                    <button
                        type="button"
                        className={styles.toggle}
                        aria-label={intl.formatMessage({ id: `${msgKey}.${mobileOpen ? "closeMenu" : "openMenu"}` })}
                        aria-expanded={mobileOpen}
                        onClick={() => setMobileOpen(!mobileOpen)}
                    >
                        <Icon name={mobileOpen ? "close" : "menu"} />
                    </button>
                </div>
            </div>

            {/* Mobile menu */}
            {mobileOpen ? (
                <nav
                    className={styles.mobileMenu}
                    aria-label={intl.formatMessage({ id: `${msgKey}.mobileNavigation` })}
                >
                    <div className={styles.mobileContent}>
                        <p className={styles.panelLabel}>
                            <FormattedMessage id={`${msgKey}.product`} />
                        </p>
                        <div className={styles.panelGrid}>
                            {FRONT_MODULES.map((module) => (
                                <Clickable
                                    key={module.slug}
                                    link={siteLink(getModulePageLink(module))}
                                    className={styles.moduleItem}
                                >
                                    <span className={styles.moduleIcon}>
                                        <Icon name={module.icon} />
                                    </span>
                                    <span className={styles.moduleName}>
                                        <FormattedMessage id={`frontModules.${module.slug}.name`} />
                                    </span>
                                </Clickable>
                            ))}
                        </div>
                        <Clickable link={siteLink(FRONT_LINKS.pricing)} className={styles.mobileItem}>
                            <FormattedMessage id={`${msgKey}.pricing`} />
                        </Clickable>
                        <Clickable href={FRONT_LINKS.blog} className={styles.mobileItem}>
                            <FormattedMessage id={`${msgKey}.blog`} />
                        </Clickable>
                        <Clickable link={siteLink(FRONT_LINKS.contact)} className={styles.mobileItem}>
                            <FormattedMessage id={`${msgKey}.contact`} />
                        </Clickable>
                        {!me ? (
                            <Button link={FRONT_LINKS.signup} color="primary" fill className={styles.mobileCta}>
                                <FormattedMessage id={`${msgKey}.startFreeTrial`} />
                            </Button>
                        ) : null}
                    </div>
                </nav>
            ) : null}
        </header>
    );
};
